import {
  Component,
  OnDestroy,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ElementRef,
  inject
} from '@angular/core';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { SatPopover } from '@zerops/zef/popover';
import { GeneralTranslations } from '@zerops/zerops/app';
import { HttpRoutingEntity } from '@zerops/zerops/core/http-routing-base';
import { HttpRouting } from '@zerops/models/http-routing';
import { selectZefProgressMapByType, toProgressMapKey } from '@zerops/zef/progress';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import {
  map,
  tap,
  withLatestFrom,
  switchMap,
  filter,
  share
} from 'rxjs/operators';
import { ObservableInput } from 'observable-input';

@Component({
  selector: 'z-http-routing-actions-pop',
  templateUrl: './http-routing-actions-pop.container.html',
  styleUrls: [ './http-routing-actions-pop.container.scss' ]
})
export class HttpRoutingActionsPopContainer extends ZefReactiveComponent implements OnDestroy {
  // # Deps
  #store = inject(Store);
  #httpRoutingEntity = inject(HttpRoutingEntity);

  // # Event Streams
  onHttpRoutingDelete$ = new Subject<void>();
  onHttpRoutingRestore$ = new Subject<void>();

  // # Data
  // -- sync
  httpRoutingDeleteType = this.#httpRoutingEntity.deleteOne.type;
  httpRoutingRestoreType = this.#httpRoutingEntity.restoreOne.type;

  // -- angular
  @ObservableInput()
  @Input('id')
  id$!: Observable<string>;

  @Input()
  activeElRef: ElementRef<any>;

  @Input()
  triggerDnsCheckActionKey: string;

  @Output()
  popClosed = new EventEmitter<void>();

  @Output()
  editClicked = new EventEmitter<HttpRouting>();

  @Output()
  triggerDnsCheckClicked = new EventEmitter<string>();

  @ViewChild('popoverRef', { static: true })
  popoverRef: SatPopover;

  // -- sync

  // -- async
  httpRouting$ = this.id$.pipe(
    filter((d) => !!d),
    switchMap((id) => this.#httpRoutingEntity.entityById$(id)),
    filter((d) => !!d)
  );
  deleteOnSync$ = this.httpRouting$.pipe(
    map((d) => d.deleteOnSync)
  );
  loadingsMap$ = this.id$.pipe(
    switchMap((id) => this.#store.pipe(
      select(selectZefProgressMapByType([
        this.httpRoutingDeleteType,
        this.httpRoutingRestoreType
      ])),
      map((m) => m && m[id] ? m[id] : {}),
    )),
    share()
  );
  isDeleteLoading$ = this.loadingsMap$.pipe(toProgressMapKey(this.httpRoutingDeleteType));
  isRestoreLoading$ = this.loadingsMap$.pipe(toProgressMapKey(this.httpRoutingRestoreType));
  generalTranslations$ = this.translate$<GeneralTranslations>('general');

  // # State resolver
  state = this.$connect({
    httpRouting: this.httpRouting$,
    deleteOnSync: this.deleteOnSync$,
    isDeleteLoading: this.isDeleteLoading$,
    isRestoreLoading: this.isRestoreLoading$,
    generalTranslations: this.generalTranslations$
  });

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      this.onHttpRoutingDelete$.pipe(
        withLatestFrom(this.id$),
        map(([ _, id ]) => this.#httpRoutingEntity.deleteOne(
          id,
          undefined,
          { type: 'pop' }
        )),
        tap(() => this.popoverRef.isOpen() ? this.popoverRef.close() : undefined)
      ),

      this.onHttpRoutingRestore$.pipe(
        withLatestFrom(this.id$),
        map(([ _, id ]) => this.#httpRoutingEntity.restoreOne(
          id,
          undefined,
          { type: 'pop' }
        )),
        tap(() => this.popoverRef.isOpen() ? this.popoverRef.close() : undefined)
      )
    ]);
  }

  // TODO https://github.com/typebytes/ngx-template-streams/issues/8
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
